import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import {
  LayoutDashboard, FileImage, Megaphone, User, Briefcase,
  FileText, Newspaper, FlaskConical, Leaf, Globe, Link2,
  Settings, LogOut, Menu, X, ChevronDown, Users, Telescope,
  Shield, Bell, Search, Building2, BookOpen, Home, Microscope, Sprout, MessageSquare, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
  { id: 'approvals', label: 'Approval Queue', icon: Clock, path: '/admin/approvals', adminOnly: true },
  { id: 'about-us', label: 'About Us', icon: Building2, path: '/admin/about-us' },
  { id: 'banners', label: 'Banners', icon: FileImage, path: '/admin/banners' },
  { id: 'announcements', label: 'Announcements', icon: Megaphone, path: '/admin/announcements' },
  { id: 'director', label: "Director's Desk", icon: User, path: '/admin/director' },
  { id: 'divisions', label: 'Divisions', icon: Building2, path: '/admin/divisions' },
  { id: 'varieties', label: 'Varieties', icon: Leaf, path: '/admin/varieties' },
  { id: 'technologies', label: 'Technologies', icon: FlaskConical, path: '/admin/technologies' },
  { id: 'research', label: 'Key Research', icon: Telescope, path: '/admin/research' },
  { id: 'research-cms', label: 'Research CMS', icon: Microscope, path: '/admin/research-management' },
  { id: 'publications', label: 'Publications', icon: BookOpen, path: '/admin/publications' },
  { id: 'itmu', label: 'ITMU / IPR Cell', icon: Shield, path: '/admin/itmu' },
  { id: 'cadre', label: 'Cadre Strength', icon: Users, path: '/admin/cadre-strength' },
  { id: 'charter', label: 'Citizen Charter', icon: FileText, path: '/admin/citizen-charter' },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, path: '/admin/jobs' },
  { id: 'tenders', label: 'Tenders', icon: FileText, path: '/admin/tenders' },
  { id: 'news', label: 'News & Events', icon: Newspaper, path: '/admin/news' },
  { id: 'photos', label: 'Photos', icon: FileImage, path: '/admin/photos' },
  { id: 'videos', label: 'Videos', icon: FileText, path: '/admin/videos' },
  { id: 'for-farmers', label: 'For Farmers', icon: Users, path: '/admin/for-farmers' },
  { id: 'vksa', label: 'VKSA', icon: Sprout, path: '/admin/vksa' },
  { id: 'services', label: 'Services', icon: Home, path: '/admin/services' },
  { id: 'links', label: 'Useful Links', icon: Link2, path: '/admin/links' },
  { id: 'social', label: 'Social Media', icon: Globe, path: '/admin/social' },
  { id: 'contact', label: 'Contact Page', icon: MessageSquare, path: '/admin/contact' },
  { id: 'rrs', label: 'RRS Malda', icon: Building2, path: '/admin/rrs-management' },
  { id: 'kvk', label: 'KVK Malda', icon: Building2, path: '/admin/kvk-management' },
  { id: 'abic', label: 'ABIC', icon: Briefcase, path: '/admin/abic-management' },
  { id: 'staff', label: 'Staff Management', icon: Users, path: '/admin/staff' },
  { id: 'users', label: 'User Management', icon: Users, path: '/admin/users', adminOnly: true },
  { id: 'audit', label: 'Audit Logs', icon: Shield, path: '/admin/audit', adminOnly: true },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isSuperAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const filteredMenuItems = menuItems.filter(item => {
    // Super Admin sees everything
    if (isSuperAdmin) {
      return true;
    }
    
    // Dashboard is always visible
    if (item.id === 'dashboard') {
      return true;
    }
    
    // Approval Queue visible to Admin+ only
    if (item.id === 'approvals') {
      return isAdmin || isSuperAdmin;
    }
    
    // User Management and Audit Logs visible to Super Admin only
    if (item.id === 'users' || item.id === 'audit') {
      return isSuperAdmin;
    }
    
    // All other items: check assigned_sections
    if (user?.assigned_sections && Array.isArray(user.assigned_sections)) {
      return user.assigned_sections.includes(item.label);
    }
    
    // Default: hide if no assigned_sections
    return false;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left - Logo & Toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 hidden md:block"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 md:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0E6D2B] flex items-center justify-center text-white text-xs font-bold">
                ICAR
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-gray-800">CISH Admin</p>
                <p className="text-xs text-gray-500">Content Management</p>
              </div>
            </div>
          </div>

          {/* Right - User Menu */}
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-[#0E6D2B] flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="ml-2 text-gray-600 hover:text-red-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <nav className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <span className="font-semibold">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2 overflow-y-auto h-full">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <a
                    key={item.id}
                    href={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                      isActive
                        ? 'bg-[#0E6D2B] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </a>
                );
              })}
            </div>
          </nav>
        </div>
      )}

      {/* Sidebar - Desktop */}
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-30 hidden md:block ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        <nav className="p-3 overflow-y-auto h-full">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <a
                key={item.id}
                href={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? 'bg-[#0E6D2B] text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title={!sidebarOpen ? item.label : ''}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {sidebarOpen && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </a>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarOpen ? 'md:ml-64' : 'md:ml-20'
        }`}
      >
        {children}
      </main>
    </div>
  );
}
